// Entry point. Authenticates, renders the header nav, then dispatches
// the current URL (?tab=, ?edit=, ?holdings=, ?history=) to the appropriate
// feature module. Feature modules are loaded on demand so the initial bundle
// stays small.

import { root } from "./core/dom.js";
import { checkAuth, renderLogin } from "./core/auth.js";
import {
  getAuthStage,
  getAuthFamily,
  getOnboardingCompleted,
  getUserOnboardingCompleted,
  getFirstValueTourDismissed,
  canEdit,
  getAuthUser,
  isNewDevice,
} from "./core/store.js";
import { renderSidebar, syncSidebarActive, initSidebarLayout } from "./core/sidebar.js";
import { initNotificationBell, startNotificationPolling } from "./core/notifications.js";

const NEW_DEVICE_TOAST_KEY = "_ndToastShown";

function showNewDeviceWelcome() {
  if (!isNewDevice()) return;
  // 已在本 session 中展示过, 不再重复
  if (sessionStorage.getItem(NEW_DEVICE_TOAST_KEY)) return;
  const user = getAuthUser();
  if (!user) return;

  sessionStorage.setItem(NEW_DEVICE_TOAST_KEY, "1");

  const toast = document.createElement("div");
  toast.className = "new-device-toast";
  toast.innerHTML = `
    <span>👋</span>
    <div>
      <strong>欢迎回来！</strong>
      <small>检测到新设备登录，阖仓已为你准备就绪。</small>
    </div>
    <button type="button" class="new-device-close" aria-label="关闭">&times;</button>
  `;
  document.body.appendChild(toast);

  function dismiss() {
    if (!toast.parentNode) return;
    toast.style.opacity = "0";
    toast.style.transition = "opacity 0.3s";
    setTimeout(() => toast.remove(), 300);
  }

  toast.querySelector(".new-device-close")?.addEventListener("click", dismiss);
  setTimeout(dismiss, 5000);
}

async function run() {
  await checkAuth();

  // Remove boot skeleton — auth has resolved, real UI is about to render
  document.querySelector(".app-layout")?.classList.remove("app-booting");
  const aside = document.getElementById("sidebar");
  if (aside) {
    aside.classList.remove("sidebar-skeleton");
    if (aside.querySelector(".sk-brand, .sk-row")) aside.innerHTML = "";
  }
  const rootEl = document.getElementById("root");
  rootEl?.querySelector(".page-skeleton")?.remove();

  if (getAuthStage() !== "authed") {
    renderLogin();
    return;
  }

  // 新设备欢迎提示
  showNewDeviceWelcome();

  // Hide the legacy site-header on the authenticated app page
  const header = document.querySelector(".site-header");
  if (header) header.hidden = true;

  renderSidebar();
  initSidebarLayout();
  initNotificationBell();
  startNotificationPolling();

  // 路径 A：无 family（未创建/未加入）或家庭未完成 onboarding → wizard
  const family = getAuthFamily();
  if (!family || !getOnboardingCompleted()) {
    document.body.dataset.tab = "onboarding";
    const onboarding = await import("./onboarding/page.js");
    await onboarding.renderOnboardingPage();
    syncSidebarActive();
    return;
  }

  // 路径 B：受邀用户（家庭已完成，但本人未看引导）→ mini-tour 后继续
  if (!getUserOnboardingCompleted()) {
    try {
      const tour = await import("./onboarding/mini-tour.js");
      await tour.runMiniTour();
    } catch (e) {
      console.warn("mini-tour failed", e);
    }
  }

  try {
    await dispatch();
  } finally {
    syncSidebarActive();
  }

  // 路径 C：First-Value Tour（不阻塞 dispatch，附着到主页）
  if (!getFirstValueTourDismissed()) {
    import("./onboarding/first-value-tour.js")
      .then((m) => m.attachTour())
      .catch(() => {});
  }

  if (location.hostname === "localhost" || location.hostname.endsWith(".workers.dev")) {
    import("./core/dev-checks.js").then(m => m.runViewportSmoke()).catch(() => {});
  }
}

async function dispatch() {

  const qs = new URLSearchParams(location.search);
  const editId = qs.get("edit");
  const historyParam = qs.get("history");
  const holdingsParam = qs.get("holdings");
  const tab = qs.get("tab");

  // Dispose any existing valuation charts when navigating away from analyze
  if (tab !== "analyze") {
    try {
      const valuation = await import("./valuation/tab.js");
      valuation.disposeAllCharts();
    } catch { /* ignore */ }
    try {
      const overview = await import("./analyze/overview-tab.js");
      overview.disposeOverviewPanel();
    } catch { /* ignore */ }
  }

  if (tab) document.body.dataset.tab = tab;
  else delete document.body.dataset.tab;

  if (editId) {
    const { enterEditMode } = await import("./strategies/list.js");
    await enterEditMode(editId);
    return;
  }

  if (historyParam) {
    const { renderHistory } = await import("./strategies/list.js");
    await renderHistory(historyParam === "all" ? null : historyParam);
    return;
  }

  if (holdingsParam === "confirm") {
    if (!canEdit()) {
      root.innerHTML = `<section class="empty"><h2>只读权限</h2><p>你可以查看持仓, 但不能录入或更新。</p></section>`;
      return;
    }
    const { enterHoldingsConfirm } = await import("./holdings/confirm.js");
    await enterHoldingsConfirm();
    return;
  }

  if (holdingsParam === "events-confirm") {
    if (!canEdit()) {
      root.innerHTML = `<section class="empty"><h2>只读权限</h2><p>你可以查看持仓, 但不能记录调仓。</p></section>`;
      return;
    }
    const { enterHoldingEventsConfirm } = await import("./holdings/events-confirm.js");
    await enterHoldingEventsConfirm();
    return;
  }

  if (tab === "portfolio" || (tab === "holdings" && qs.get("view") !== "detail")) {
    const { renderPortfolioXrayTab } = await import("./portfolio/xray-tab.js");
    await renderPortfolioXrayTab();
    return;
  }

  if (tab === "holdings") {
    const { renderHoldingsList } = await import("./holdings/list-tab.js");
    await renderHoldingsList();
    return;
  }

  if (tab === "data") {
    const { renderDataHealthTab } = await import("./data-health/tab.js");
    await renderDataHealthTab();
    return;
  }

  if (tab === "decisions") {
    const { renderDecisionsListTab } = await import("./decisions/list-tab.js");
    await renderDecisionsListTab();
    return;
  }

  if (tab === "watches") {
    location.href = "/watches";
    return;
  }

  if (tab === "watchlist") {
    const itemId = qs.get("id");
    const wl = await import("./watchlist/tab.js");
    if (itemId) await wl.renderWatchlistDetail(itemId);
    else await wl.renderWatchlistTab();
    return;
  }

  if (tab === "analyze") {
    const view = qs.get("view") || "overview";
    const urlSymbol = (qs.get("symbol") || "").trim().toLowerCase();
    const { isSupportedSymbol } = await import("./holdings/list-tab.js");
    if (view === "overview") {
      // 离开 valuation 视图时清理 echarts 实例
      try {
        const valuation = await import("./valuation/tab.js");
        valuation.disposeAllCharts();
      } catch { /* ignore */ }
      const overview = await import("./analyze/overview-tab.js");
      const sym = isSupportedSymbol(urlSymbol) ? urlSymbol : "";
      await overview.renderOverviewTab(sym);
      return;
    }
    // 离开 overview 视图时清理 K 线 panel
    try {
      const overview = await import("./analyze/overview-tab.js");
      overview.disposeOverviewPanel();
    } catch { /* ignore */ }
    if (view === "backtest") {
      const backtest = await import("./analyze/backtest-tab.js");
      if (urlSymbol && isSupportedSymbol(urlSymbol)) {
        backtest.analyzeState.form.symbol = urlSymbol;
        localStorage.setItem("analyze_symbol", urlSymbol);
      }
      backtest.renderAnalyzeTab();
      if (backtest.analyzeState.form.symbol) backtest.loadAnalyzeHistory(backtest.analyzeState.form.symbol, true);
    } else if (view === "earnings") {
      const earnings = await import("./analyze/earnings-tab.js");
      if (urlSymbol && isSupportedSymbol(urlSymbol)) {
        earnings.earningsState.symbol = urlSymbol;
        localStorage.setItem("earnings_symbol", urlSymbol);
      }
      earnings.renderEarningsTab();
      if (earnings.earningsState.symbol) earnings.loadEarnings(earnings.earningsState.symbol);
    } else {
      // Dispose any existing valuation charts before switching (might be earnings→valuation)
      try {
        const valuation = await import("./valuation/tab.js");
        valuation.disposeAllCharts();
        if (urlSymbol && isSupportedSymbol(urlSymbol)) {
          valuation.valuationState.symbol = urlSymbol;
          localStorage.setItem("valuation_symbol", urlSymbol);
        }
        const lb = qs.get("lookback");
        if (lb && ["10y", "5y", "3y"].includes(lb)) valuation.valuationState.lookback = lb;
        valuation.renderValuationTab();
        if (valuation.valuationState.symbol) valuation.loadValuation(valuation.valuationState.symbol);
      } catch { /* ignore */ }
    }
    return;
  }

  if (tab === "eventchain") {
    const ec = await import("./eventchain/tab.js");
    const eventId = qs.get("id");
    if (eventId) await ec.renderEventChainDetail(eventId);
    else await ec.renderEventChainTab();
    return;
  }

  if (tab === "strategies") {
    location.href = "/watches";
    return;
  }

  if (tab === "digest") {
    const { renderDigestTab } = await import("./digest/tab.js");
    await renderDigestTab();
    return;
  }

  if (tab === "training") {
    location.href = "/watches?view=training";
    return;
  }

  if (tab === "settings") {
    const { renderSettingsTab } = await import("./settings/tab.js");
    await renderSettingsTab();
    return;
  }

  if (tab === "chat") {
    const { renderChatPage } = await import("./chat/page.js");
    await renderChatPage();
    return;
  }

  // 默认:无 tab 且无策略草稿时, 跳持仓(家庭投资从"我有什么"开始)
  const hasStrategyDraft =
    sessionStorage.getItem("parsed_strategy") ||
    sessionStorage.getItem("pending_strategy_raw");
  if (!tab && !hasStrategyDraft) {
    document.body.dataset.tab = "portfolio";
    const { renderPortfolioXrayTab } = await import("./portfolio/xray-tab.js");
    await renderPortfolioXrayTab();
    return;
  }

  // 清掉 magic-link 回跳的 ?err=xxx 之类
  if (location.search) history.replaceState({}, "", location.pathname);

  // 登录前在 landing 暂存的原文, 登录后自动解析
  const confirmMod = await import("./strategies/confirm.js");
  const pendingRaw = sessionStorage.getItem("pending_strategy_raw");
  if (!confirmMod.state && pendingRaw && !sessionStorage.getItem("parsed_strategy")) {
    if (!canEdit()) {
      sessionStorage.removeItem("pending_strategy_raw");
      root.innerHTML = `<section class="empty"><h2>只读权限</h2><p>你可以查看家庭账本, 但不能解析或新增策略。</p></section>`;
      return;
    }
    root.innerHTML = `<section class="empty"><p>解析登录前暂存的策略中…</p></section>`;
    try {
      const res = await fetch("/api/parse", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ raw_text: pendingRaw }),
      });
      if (res.ok) {
        const data = await res.json();
        sessionStorage.setItem("parsed_strategy", JSON.stringify(data));
        confirmMod.setState(confirmMod.loadState());
      } else {
        sessionStorage.removeItem("pending_strategy_raw");
      }
    } catch {
      sessionStorage.removeItem("pending_strategy_raw");
    }
  }

  if (confirmMod.state) {
    confirmMod.render();
    confirmMod.loadQuotes();
    return;
  }

  const { renderList } = await import("./strategies/list.js");
  await renderList();
}

run();
